import * as React from 'react';
import type { Editor } from '@tiptap/react';
import { BookOpenIcon, SpellCheckIcon } from 'lucide-react';

import { useSession } from '@/components/auth/user-provider';
import { Button } from '@/components/ui/button';
import { setPersonalSpellingWords } from './document-spell-check';
import { usePersonalDictionary } from './personal-dictionary';
import { PersonalDictionaryDialog } from './personal-dictionary-dialog';
import { SpellingContextMenu } from './spelling-context-menu';
import { useSpellCheckPreference } from './spell-check-preference';
import { AiProofingController, AiProofingRibbon, AiProofingStore } from './ai-proofing-tools';

/**
 * Proofing for one editor: keeps the remembered on/off choice and the person's dictionary in step with
 * spell check, and provides the Review ribbon controls and the right-click spelling menu. When an
 * `aiDocumentId` is given (the person may use AI on that document), AI proofing is offered on request.
 */
export function useDocumentProofing(editor: Editor | null, readOnly: boolean, aiDocumentId?: string) {
  const [aiStore] = React.useState(() => new AiProofingStore());
  const [enabled, setEnabled] = useSpellCheckPreference();
  const { session } = useSession();
  const user = session.status === 'signedIn' ? session.user : null;
  const aiProofing = Boolean(user && aiDocumentId && editor && !editor.isDestroyed);
  const dictionary = usePersonalDictionary(user?.email ?? 'signed-out');
  const [dictionaryOpen, setDictionaryOpen] = React.useState(false);

  React.useEffect(() => { setPersonalSpellingWords(user ? dictionary.words : []); }, [dictionary.words, user]);
  React.useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.commands.setSpellCheckEnabled(enabled);
  }, [editor, enabled]);

  const addToDictionary = user ? (word: string) => { dictionary.add(word); } : undefined;

  const tools = <>
    <Button type="button" variant="ghost" size="sm" className="h-9" aria-pressed={enabled} onClick={() => setEnabled(!enabled)}
      title={enabled ? 'Turn spell check and autocorrect off' : 'Turn spell check and autocorrect on'}>
      <SpellCheckIcon aria-hidden /> Spelling {enabled ? 'on' : 'off'}
    </Button>
    {user && <Button type="button" variant="ghost" size="sm" className="h-9" onClick={() => setDictionaryOpen(true)}>
      <BookOpenIcon aria-hidden /> Personal dictionary
    </Button>}
    {aiProofing && editor && <AiProofingRibbon editor={editor} store={aiStore} readOnly={readOnly} />}
  </>;

  const overlays = <>
    {editor && !editor.isDestroyed && <SpellingContextMenu editor={editor} readOnly={readOnly || !enabled} onAddToDictionary={addToDictionary} />}
    {user && <PersonalDictionaryDialog open={dictionaryOpen} onOpenChange={setDictionaryOpen} dictionary={dictionary} />}
    {aiProofing && editor && aiDocumentId && <AiProofingController editor={editor} store={aiStore} documentId={aiDocumentId} readOnly={readOnly} />}
  </>;

  return { enabled, tools, overlays };
}
